import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Primary } from '../../../button';
import { LoadingContainer } from '../../../loading/LoadingContainer';
import { Modal, ModalContents } from '../../../modal';
import { mediaQuery, typography } from '../../../../theme';
import { useLunchDrinks } from '../../hooks/useLunchDrinks';
import { useFreeDrinkAmounts } from '../../hooks/useFreeDrinkAmounts';
import { UpsellProduct } from '../upsellProducts/UpsellProduct';
import { Drink } from '../../../../icons/Drink';
import { useAddProduct } from '../../../../state/order/hooks/useOrderMutation';
import { ProductItem } from '../../types';
import { useModalState } from '../../../modal/useModalState';
import { ModalName } from '../../../modal/modalNames';
import { AnalyticsListName, analyticsEvents } from '../../../../utils/analytics/analytics';
import { Capacitor } from '@capacitor/core';
import { colors } from '../../../../theme/color';

enum freeDrinkStage {
  SMALL,
  CUTSCENE,
  LARGE,
}

export const LunchDrinkModal: React.FC = () => {
  const { freeSmallAmount, freeLargeAmount } = useFreeDrinkAmounts();

  const [stage, setStage] = useState<freeDrinkStage>(getInitialStage(freeSmallAmount));

  const onOpen = useCallback(() => {
    setStage(getInitialStage(freeSmallAmount));
  }, [freeSmallAmount]);

  return (
    <Modal name={ModalName.LUNCH_DRINK} onOpen={onOpen}>
      <StyledModalContents
        title={getTitle(freeSmallAmount, freeLargeAmount, stage)}
        scroll="paper"
        data-testid="lunch-drink-modal"
      >
        <ModalInterior
          freeLargeAmount={freeLargeAmount}
          freeSmallAmount={freeSmallAmount}
          stage={stage}
          setStage={setStage}
        />
      </StyledModalContents>
    </Modal>
  );
};

type Props = {
  freeSmallAmount: number;
  freeLargeAmount: number;
  stage: freeDrinkStage;
  setStage: React.Dispatch<React.SetStateAction<freeDrinkStage>>;
};

const ModalInterior: React.FC<Props> = ({ freeSmallAmount, freeLargeAmount, stage, setStage }) => {
  const { closeModal } = useModalState();
  const { smallDrinks, largeDrinks, isLoading } = useLunchDrinks();
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const addItemToCart = useAddProduct();

  useEffect(() => {
    analyticsEvents.viewItemList([...smallDrinks, ...largeDrinks], {
      name: AnalyticsListName.LunchOffering,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddToCart = useCallback(
    async (item: ProductItem, listPosition: number): Promise<void> => {
      setButtonLoading(true);
      const list = { name: AnalyticsListName.LunchOffering };

      const { item: orderItem } = await addItemToCart(item, { list });
      analyticsEvents.addToCart([orderItem], list);
      analyticsEvents.selectItem(orderItem, list, listPosition);

      if (stage === freeDrinkStage.SMALL && freeSmallAmount - 1 === 0 && freeLargeAmount > 0) {
        setTimeout(() => {
          setStage(freeDrinkStage.CUTSCENE);
          setButtonLoading(false);
        }, 1500);
      } else if (
        stage === freeDrinkStage.SMALL &&
        freeSmallAmount - 1 === 0 &&
        freeLargeAmount === 0
      ) {
        closeModal(ModalName.LUNCH_DRINK);
        setButtonLoading(false);
      } else if (stage === freeDrinkStage.LARGE && freeLargeAmount - 1 === 0) {
        closeModal(ModalName.LUNCH_DRINK);
        setButtonLoading(false);
      } else {
        setButtonLoading(false);
      }
    },
    [addItemToCart, stage, freeSmallAmount, freeLargeAmount, setStage, closeModal]
  );

  const handleDone = (): void => {
    if (buttonLoading) {
      return;
    }

    if (
      (stage === freeDrinkStage.CUTSCENE || stage === freeDrinkStage.SMALL) &&
      freeLargeAmount > 0
    ) {
      setStage(freeDrinkStage.LARGE);
    } else {
      closeModal(ModalName.LUNCH_DRINK);
    }
  };

  const drinks = stage === freeDrinkStage.SMALL ? smallDrinks : largeDrinks;
  const freeAmount = stage === freeDrinkStage.SMALL ? freeSmallAmount : freeLargeAmount;

  return (
    <StyledLoadingContainer loading={isLoading}>
      {stage !== freeDrinkStage.CUTSCENE && <Remaining>Jäljellä {freeAmount} kpl</Remaining>}
      {stage === freeDrinkStage.CUTSCENE && (
        <CutsceneContainer onClick={() => setStage(freeDrinkStage.LARGE)}>
          <StyledDrink />
          <Header>
            {freeLargeAmount === 1 ? (
              <>
                <span>Tilaukseen kuuluu vielä </span>
                <br />
                <span> 1.5l juoma</span>
              </>
            ) : (
              <>
                <span>Tilaukseen kuuluu vielä </span>
                <br /> {freeLargeAmount}
                <span> kpl 1.5l juomia</span>
              </>
            )}
          </Header>
        </CutsceneContainer>
      )}
      {stage !== freeDrinkStage.CUTSCENE &&
        drinks.map((item, index) => (
          <UpsellProduct
            key={`lunch-drink-item-${index}-${item.productID}`}
            item={item}
            onItemIncrease={async (item) => await handleAddToCart(item, index + 1)}
            discount
            addedOverlay
          />
        ))}
      <ButtonBackground>
        <DrinksDoneButton onClick={handleDone} disabled={buttonLoading}>
          {getButtonText(freeSmallAmount, freeLargeAmount, stage)}
        </DrinksDoneButton>
      </ButtonBackground>
    </StyledLoadingContainer>
  );
};

const getButtonText = (small: number, large: number, stage: freeDrinkStage): string => {
  if (stage === freeDrinkStage.CUTSCENE) {
    return 'Siirry valitsemaan isoja juomia';
  }

  if (stage === freeDrinkStage.SMALL) {
    return `Valmis (${small} kpl pieniä juomia jäljellä)`;
  }

  if (large > 0) {
    return `Valmis (${large} kpl isoja juomia jäljellä)`;
  }

  return 'Valmis';
};

const getTitle = (small: number, large: number, stage: freeDrinkStage): string => {
  if (stage === freeDrinkStage.CUTSCENE) {
    return '';
  }

  if (stage === freeDrinkStage.SMALL) {
    if (large > 0) {
      return 'Valitse ensin pienet juomat';
    }

    return 'Valitse juomat';
  }

  return 'Valitse juomat';
};

const getInitialStage = (small: number): freeDrinkStage => {
  if (small > 0) {
    return freeDrinkStage.SMALL;
  }

  return freeDrinkStage.LARGE;
};

const Remaining = styled.div`
  margin-top: -15px;
`;

const StyledDrink = styled(Drink)`
  width: 180px;
  height: 180px;
  color: ${colors.brandPrimary};
  justify-self: center;
  margin-bottom: 20px;
`;

const Header = styled.div`
  ${typography.headingMediumStyle}
  text-align: center;
`;

const CutsceneContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: calc(100vh - 80px);
  justify-content: center;
`;

const ButtonBackground = styled.div`
  position: sticky;
  bottom: 0px;
  padding: ${Capacitor.isNativePlatform() ? '16px 0 env(safe-area-inset-bottom) 0' : '16px 0'};
  left: 0;
  right: 0;
  margin-bottom: env(safe-area-inset-bottom);
  background: ${colors.white};
  background: linear-gradient(0deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0) 100%);
`;

const StyledModalContents = styled(ModalContents)`
  position: relative;
  width: 520px;
  max-width: 100vw;
  padding-bottom: 0;

  @media ${mediaQuery.medium} {
    width: 100%;
  }
`;

const DrinksDoneButton = styled(Primary)`` as typeof Primary;

const StyledLoadingContainer = styled(LoadingContainer)`
  min-height: 200px;
`;
